import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, KeyRound, Upload, Receipt, CheckCircle2, Shield, Download, Trash2, ShieldCheck, Monitor, LogOut, Clock } from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import { sanitizePhone, formatPhoneTR, isValidTurkishPhone, sanitizeDecimal } from "@/lib/validators";
import { tokenStorage } from "@/lib/tokenStorage";

const MODELS = [
  { provider: "gemini", model: "gemini-3.1-pro-preview", label: "Gemini 3.1 Pro (Önerilen)" },
  { provider: "openai", model: "gpt-5.6-terra", label: "GPT 5.6 Terra" },
  { provider: "anthropic", model: "claude-sonnet-5", label: "Claude Sonnet 5" },
];

const DATA_SOURCES = [
  { key: "haremaltin", label: "Harem Altın (Önerilen) — Canlı Socket.IO akışı (Altın + Gümüş, saniyelik)" },
  { key: "truncgil", label: "Truncgil (Kapalıçarşı) — HTTP polling (Altın + Gümüş, 60sn)" },
  { key: "manual", label: "Manuel — Sadece admin panelinden güncelleme" },
  { key: "mock", label: "Simülasyon — Rastgele dalgalanma (demo/eğitim)" },
];

const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function Settings() {
  const [aiProvider, setAiProvider] = useState("gemini");
  const [aiModel, setAiModel] = useState("gemini-3.1-pro-preview");
  const [dataSource, setDataSource] = useState("haremaltin");
  const [rates, setRates] = useState({});
  const [rateEdit, setRateEdit] = useState({ metal_type: "gold", buying_price: 0, selling_price: 0 });
  const [refreshing, setRefreshing] = useState(false);
  const [profitMargin, setProfitMargin] = useState(3);
  const [savingMargin, setSavingMargin] = useState(false);
  const [shop, setShop] = useState({ shop_name: "", shop_address: "", shop_phone: "", shop_tax_no: "", shop_receipt_footer: "", shop_logo_url: "" });
  const [savingShop, setSavingShop] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [backupRunning, setBackupRunning] = useState(false);
  const [downloadRunning, setDownloadRunning] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catDraft, setCatDraft] = useState({ name: "", default_workmanship_type: "pct", default_workmanship_value: 0, default_margin_pct: 15 });
  const logoInputRef = useRef(null);

  // Security state
  const [security, setSecurity] = useState({ two_factor_enabled: false });
  const [loginHistory, setLoginHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [currentJti, setCurrentJti] = useState(null);
  const [twofaModalOpen, setTwofaModalOpen] = useState(false);
  const [twofaPassword, setTwofaPassword] = useState("");
  const [twofaBusy, setTwofaBusy] = useState(false);

  // Kendi Ödeme Altyapın (per-shop PayTR) — POS'ta müşteri tahsilatı için
  const [paytr, setPaytr] = useState({ merchant_id: "", merchant_key_masked: "", merchant_salt_masked: "", enabled: false, configured: false });
  const [paytrDraft, setPaytrDraft] = useState({ merchant_id: "", merchant_key: "", merchant_salt: "", enabled: false });
  const [savingPaytr, setSavingPaytr] = useState(false);

  const load = async () => {
    try {
      const s = await api.get("/settings");
      const ai = s.data.find((x) => x.key === "ai_model");
      if (ai) { setAiProvider(ai.provider || "gemini"); setAiModel(ai.value); }
      const ds = s.data.find((x) => x.key === "data_source");
      if (ds) setDataSource(ds.value || "haremaltin");
    } catch (e) {}
    const r = await api.get("/rates");
    setRates(r.data);
    try {
      const pm = await api.get("/settings/profit-margin");
      setProfitMargin(Number(pm.data?.percent ?? 3));
    } catch {}
    try {
      const t = await api.get("/tenant");
      setShop({
        shop_name: t.data?.shop_name || "",
        shop_address: t.data?.shop_address || "",
        shop_phone: t.data?.shop_phone || "",
        shop_tax_no: t.data?.shop_tax_no || "",
        shop_receipt_footer: t.data?.shop_receipt_footer || "",
        shop_logo_url: t.data?.shop_logo_url || "",
      });
    } catch {}
    try {
      const inv = await api.get("/billing/invoices");
      setInvoices(inv.data?.invoices || []);
    } catch {}
    try {
      const cats = await api.get("/categories");
      setCategories(cats.data || []);
    } catch {}
    // Security
    try {
      const [s, h, ss] = await Promise.all([
        api.get("/security/status"),
        api.get("/security/login-history"),
        api.get("/security/sessions"),
      ]);
      setSecurity(s.data || {});
      setLoginHistory(h.data?.events || []);
      setSessions(ss.data?.sessions || []);
      setCurrentJti(ss.data?.current_jti || null);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const downloadBackupNow = async () => {
    setDownloadRunning(true);
    try {
      const token = tokenStorage.get("uc_token");
      const r = await fetch(`${API}/backups/download-now`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("İndirilemedi");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `MİLYEMSOFT-Yedek-${new Date().toISOString().slice(0,16).replace(/[:T]/g,'')}.xlsx`;
      a.click();
      toast.success(`Yedek indirildi (${(blob.size/1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.error(e.message || "İndirilemedi");
    } finally { setDownloadRunning(false); }
  };

  const submitTwoFactor = async (action) => {
    if (!twofaPassword) return toast.error("Parolanızı girin");
    setTwofaBusy(true);
    try {
      const r = await api.post("/security/two-factor", { action, password: twofaPassword });
      toast.success(r.data?.message || "Ayar kaydedildi");
      setTwofaModalOpen(false);
      setTwofaPassword("");
      const s = await api.get("/security/status");
      setSecurity(s.data || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setTwofaBusy(false); }
  };

  const revokeAllSessions = async () => {
    if (!window.confirm("Tüm diğer oturumlar kapatılsın mı? Mevcut oturumunuz açık kalır.")) return;
    try {
      const r = await api.post("/security/revoke-all");
      toast.success(`${r.data.revoked} oturum sonlandırıldı`);
      const ss = await api.get("/security/sessions");
      setSessions(ss.data?.sessions || []);
    } catch (e) {
      toast.error("Sonlandırılamadı");
    }
  };

  const revokeSession = async (jti) => {
    try {
      await api.post(`/security/revoke/${jti}`);
      setSessions((prev) => prev.filter((s) => s.jti !== jti));
      toast.success("Oturum sonlandırıldı");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sonlandırılamadı");
    }
  };

  const runBackup = async () => {
    setBackupRunning(true);
    try {
      const r = await api.post("/backups/run-now");
      if (r.data?.sent) {
        toast.success(`Yedek e-postanıza gönderildi (${r.data.email}) — dosya boyutu ${(r.data.size_bytes/1024).toFixed(1)} KB`);
      } else {
        toast.warning("Yedek üretildi fakat e-posta servisi yanıt vermedi");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yedek alınamadı");
    } finally { setBackupRunning(false); }
  };

  const saveCategory = async () => {
    if (!catDraft.name.trim()) return toast.error("Kategori adı gerekli");
    try {
      await api.post("/categories", {
        name: catDraft.name.trim(),
        default_workmanship_type: catDraft.default_workmanship_type,
        default_workmanship_value: Number(catDraft.default_workmanship_value || 0),
        default_margin_pct: Number(catDraft.default_margin_pct || 0),
      });
      toast.success(`${catDraft.name} kaydedildi`);
      setCatDraft({ name: "", default_workmanship_type: "pct", default_workmanship_value: 0, default_margin_pct: 15 });
      const cats = await api.get("/categories");
      setCategories(cats.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Kategori silinsin mi?")) return;
    try {
      await api.delete(`/categories/${id}`);
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error("Silinemedi");
    }
  };

  const editCategory = (c) => {
    setCatDraft({
      name: c.name,
      default_workmanship_type: c.default_workmanship_type,
      default_workmanship_value: c.default_workmanship_value,
      default_margin_pct: c.default_margin_pct,
    });
  };

  const saveShop = async () => {
    setSavingShop(true);
    try {
      await api.put("/tenant", shop);
      toast.success("Mağaza bilgileri güncellendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSavingShop(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    if (!["image/png","image/jpeg","image/webp"].includes(file.type)) {
      toast.error("Sadece PNG, JPEG veya WEBP");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Logo maks 4MB olmalı");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/tenant/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShop((s) => ({ ...s, shop_logo_url: r.data.url }));
      toast.success("Logo yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    } finally { setLogoUploading(false); }
  };

  // ── Kendi Ödeme Altyapın (PayTR) ────────────────────────────────
  const loadPaytr = async () => {
    try {
      const r = await api.get("/settings/payment-provider");
      setPaytr(r.data);
      setPaytrDraft({ merchant_id: r.data.merchant_id || "", merchant_key: "", merchant_salt: "", enabled: !!r.data.enabled });
    } catch (e) { /* admin değilse 403 — sessiz */ }
  };

  useEffect(() => { loadPaytr(); }, []);

  const savePaytr = async () => {
    if (paytrDraft.enabled) {
      const finalMid = paytrDraft.merchant_id || paytr.merchant_id;
      const hasKey = paytrDraft.merchant_key || paytr.merchant_key_masked;
      const hasSalt = paytrDraft.merchant_salt || paytr.merchant_salt_masked;
      if (!finalMid || !hasKey || !hasSalt) {
        return toast.error("Merchant ID, Key ve Salt üçü de dolu olmalı");
      }
    }
    setSavingPaytr(true);
    try {
      await api.put("/settings/payment-provider", paytrDraft);
      toast.success("Ödeme altyapın kaydedildi");
      await loadPaytr();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSavingPaytr(false); }
  };

  const fmtTL = (v) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(Number(v) || 0);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" }) : "—";
  const statusLabel = (s) => ({
    active: { txt: "Aktif", color: "text-emerald-400", icon: <CheckCircle2 size={12} /> },
    paid:   { txt: "Ödendi", color: "text-emerald-400", icon: <CheckCircle2 size={12} /> },
    pending: { txt: "Bekliyor", color: "text-yellow-400", icon: null },
    token_failed: { txt: "Başarısız", color: "text-red-400", icon: null },
    canceled: { txt: "İptal", color: "text-red-400", icon: null },
    expired: { txt: "Süresi Doldu", color: "text-neutral-500", icon: null },
  }[s || ""] || { txt: s || "—", color: "text-neutral-400", icon: null });

  const saveProfitMargin = async () => {
    const val = Number(profitMargin);
    if (!Number.isFinite(val) || val < 0 || val > 100) {
      toast.error("Kâr marjı 0-100 arası olmalı");
      return;
    }
    setSavingMargin(true);
    try {
      await api.post("/settings/profit-margin", { percent: val });
      toast.success(`Global kâr marjı ${val}% olarak kaydedildi. Sayfayı yenileyin.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally { setSavingMargin(false); }
  };

  const saveAi = async () => {
    await api.post("/settings/ai_model", { provider: aiProvider, model: aiModel });
    toast.success("AI modeli güncellendi");
  };

  const changeSource = async (val) => {
    setDataSource(val);
    await api.post("/settings/data_source", { value: val });
    toast.success("Kur kaynağı güncellendi");
  };

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/rates/refresh");
      toast.success("Canlı kaynaktan yenilendi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yenileme hatası");
    } finally { setRefreshing(false); }
  };

  const updateRate = async () => {
    await api.post("/rates", rateEdit);
    toast.success("Kur güncellendi");
    load();
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <div className="label-caps">Yönetici</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Ayarlar</h1>
      </div>

      <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="shop-info-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div>
            <div className="label-caps">Dükkan · Fiş / Fatura Bilgileri</div>
            <div className="font-display text-2xl mt-1">Mağaza Kimliği</div>
          </div>
          <button onClick={()=>setPwOpen(true)} className="text-xs px-3 py-2 border border-white/10 hover:border-[--gold] hover:text-[--gold] inline-flex items-center gap-2" data-testid="open-change-pw">
            <KeyRound size={12} /> Şifre Değiştir
          </button>
        </div>
        <p className="text-xs text-neutral-500 mb-5">
          Bu bilgiler WhatsApp dijital fişinin başlığında, PDF faturada ve tüm resmi belgelerde görünür.
        </p>

        {/* Logo uploader */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/6">
          <div className="w-20 h-20 border border-white/10 bg-[#0a0a0a] flex items-center justify-center overflow-hidden" data-testid="shop-logo-preview">
            {shop.shop_logo_url ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}${shop.shop_logo_url}`} alt="logo" className="w-full h-full object-contain" />
            ) : (
              <div className="text-[10px] text-neutral-500 text-center">Logo yok</div>
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm mb-1">Dükkan Logosu</div>
            <div className="text-[11px] text-neutral-500 mb-2">PNG / JPEG / WEBP · Maks 4MB · Kare formatta önerilir</div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e)=>uploadLogo(e.target.files?.[0])}
              data-testid="shop-logo-input"
            />
            <button onClick={()=>logoInputRef.current?.click()} disabled={logoUploading} className="text-xs px-3 py-2 border border-white/10 hover:border-[--gold] inline-flex items-center gap-2" data-testid="upload-logo-btn">
              <Upload size={12} /> {logoUploading ? "Yükleniyor…" : (shop.shop_logo_url ? "Logoyu Değiştir" : "Logo Yükle")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Dükkan Adı</label>
            <input type="text" value={shop.shop_name} onChange={(e)=>setShop({...shop, shop_name: e.target.value})}
                    className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]"
                    data-testid="shop-name-input" />
          </div>
          <div className="md:col-span-2">
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Adres</label>
            <textarea rows={2} value={shop.shop_address} onChange={(e)=>setShop({...shop, shop_address: e.target.value})}
                       placeholder="Örn: Kapalıçarşı Kuyumcular Cd. No:12 Fatih / İstanbul"
                       className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
                       data-testid="shop-address-input" />
          </div>
          <div>
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Telefon</label>
            <input type="tel" inputMode="tel" maxLength={13}
                    value={formatPhoneTR(shop.shop_phone || "")}
                    onChange={(e)=>setShop({...shop, shop_phone: sanitizePhone(e.target.value)})}
                    placeholder="534 964 67 03"
                    className={`w-full bg-transparent border px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600 ${
                      shop.shop_phone && !isValidTurkishPhone(shop.shop_phone) ? "border-red-500/60" : "border-white/10"
                    }`}
                    data-testid="shop-phone-input" />
            {shop.shop_phone && !isValidTurkishPhone(shop.shop_phone) && (
              <div className="mt-1 text-[10px] text-red-400/90">10 haneli TR numarası girin</div>
            )}
          </div>
          <div>
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Vergi No / VKN</label>
            <input type="text" value={shop.shop_tax_no} onChange={(e)=>setShop({...shop, shop_tax_no: e.target.value})}
                    placeholder="1234567890"
                    className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
                    data-testid="shop-tax-input" />
          </div>
          <div className="md:col-span-2">
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Fiş Alt Notu (opsiyonel)</label>
            <input type="text" value={shop.shop_receipt_footer} onChange={(e)=>setShop({...shop, shop_receipt_footer: e.target.value})}
                    placeholder="www.mustafayildiz.shop · @mustafakuyumculuk"
                    className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
                    data-testid="shop-footer-input" />
          </div>
        </div>
        <button onClick={saveShop} disabled={savingShop} className="btn-primary-gold mt-6" data-testid="save-shop-info">
          {savingShop ? "Kaydediliyor…" : "Mağaza Bilgilerini Kaydet"}
        </button>
      </div>

      {/* ── Kendi Ödeme Altyapın (per-shop PayTR) ─────────────────────── */}
      <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="paytr-card">
        <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
          <div>
            <div className="label-caps flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-5 px-1.5 rounded-none text-black font-bold text-[10px]" style={{background:"var(--gold)"}}>PayTR</span>
              Kendi Ödeme Altyapın
            </div>
            <div className="font-display text-2xl mt-1.5">POS Online Tahsilat</div>
          </div>
          {paytr.configured && (
            <span className={`text-[10px] uppercase tracking-widest border px-2 py-1 ${paytr.enabled ? "border-emerald-400/60 text-emerald-300" : "border-white/20 text-neutral-400"}`}>
              {paytr.enabled ? "● Aktif" : "○ Kapalı"}
            </span>
          )}
        </div>
        <p className="text-xs text-neutral-500 mb-5 max-w-3xl">
          POS'ta müşteri kartla ödediğinde para <strong className="text-neutral-200">doğrudan kendi banka hesabınıza</strong> düşer.
          Anahtarlarınız yalnızca sizin dükkanınıza aittir; sunucumuz secret'ları asla göstermez, sadece maskeli olarak listeler.
          <br />
          <span className="text-neutral-600">Bilgileri PayTR panelinde <em>Bilgi &gt; API Bilgileri</em> altında bulabilirsiniz.</span>
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Merchant ID</label>
            <input type="text" inputMode="numeric"
              value={paytrDraft.merchant_id}
              onChange={(e)=>setPaytrDraft(d => ({...d, merchant_id: e.target.value.replace(/\D/g, "")}))}
              placeholder={paytr.merchant_id || "6 haneli PayTR ID"}
              className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
              data-testid="paytr-mid" />
          </div>
          <div>
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Merchant Key</label>
            <input type="password" autoComplete="new-password"
              value={paytrDraft.merchant_key}
              onChange={(e)=>setPaytrDraft(d => ({...d, merchant_key: e.target.value}))}
              placeholder={paytr.merchant_key_masked || "PayTR API Key"}
              className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
              data-testid="paytr-key" />
          </div>
          <div>
            <label className="block mb-1.5 text-[11px] font-medium tracking-wide uppercase text-neutral-400">Merchant Salt</label>
            <input type="password" autoComplete="new-password"
              value={paytrDraft.merchant_salt}
              onChange={(e)=>setPaytrDraft(d => ({...d, merchant_salt: e.target.value}))}
              placeholder={paytr.merchant_salt_masked || "PayTR API Salt"}
              className="w-full bg-transparent border border-white/10 px-3 py-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] placeholder-neutral-600"
              data-testid="paytr-salt" />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox"
              checked={paytrDraft.enabled}
              onChange={(e)=>setPaytrDraft(d => ({...d, enabled: e.target.checked}))}
              data-testid="paytr-enabled" />
            <span className="text-xs">POS'ta bu altyapıyı kullan</span>
          </label>
          <div className="flex-1" />
          <button onClick={savePaytr} disabled={savingPaytr} className="btn-primary-gold" data-testid="save-paytr">
            {savingPaytr ? "Kaydediliyor…" : "Ödeme Altyapını Kaydet"}
          </button>
        </div>

        <div className="text-[10px] text-neutral-600 mt-3 tracking-wide">
          Master anahtarlar (MİLYEMSOFT abonelik) bu sekmeden bağımsızdır ve dükkan sahiplerine asla verilmez.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="profit-margin-card">
          <div className="label-caps mb-2">Fiyatlandırma</div>
          <div className="font-display text-2xl mb-2">Global Kâr Marjı</div>
          <p className="text-xs text-neutral-500 mb-4">
            Tüm ürün fiyatlarının üzerine otomatik eklenen dükkan bazlı kâr/işçilik payı.
            Ürün bazında girilen `Kar Oranı` sonrası hesaplanır. Örn: 3% seçilirse 10.000 ₺ ürün 10.300 ₺ satılır.
          </p>
          <div className="flex items-center gap-3">
            <input
              type="text"
              inputMode="decimal"
              value={profitMargin}
              onChange={(e)=>setProfitMargin(sanitizeDecimal(e.target.value, { maxDecimals: 2 }))}
              placeholder="3,0"
              className="w-32 bg-transparent border border-white/10 px-3 py-3 text-lg font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]"
              data-testid="profit-margin-input"
            />
            <span className="text-2xl text-neutral-400">%</span>
            <button onClick={saveProfitMargin} disabled={savingMargin} className="btn-primary-gold ml-4" data-testid="save-profit-margin">
              {savingMargin ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
          <div className="text-[10px] text-neutral-600 mt-3">Varsayılan: 3%. Değişiklik sonrası Ürünler / POS sayfası yenilendiğinde yeni fiyat görünür.</div>
        </div>

        <div className="card-flat p-4 sm:p-6 lg:p-8">
          <div className="label-caps mb-2">AI Modeli</div>
          <div className="font-display text-2xl mb-4">Vitrin Tanıma Motoru</div>
          <div className="space-y-3">
            {MODELS.map((m) => (
              <label key={m.model} className="flex items-center gap-3 border border-white/10 px-4 py-3 cursor-pointer hover:border-[--gold]" data-testid={`ai-${m.model}`}>
                <input type="radio" name="ai" checked={aiProvider === m.provider && aiModel === m.model} onChange={()=>{setAiProvider(m.provider); setAiModel(m.model);}} />
                <div className="flex-1">
                  <div className="text-sm">{m.label}</div>
                  <div className="font-mono text-xs text-neutral-500">{m.provider} · {m.model}</div>
                </div>
              </label>
            ))}
          </div>
          <button onClick={saveAi} className="btn-primary-gold mt-6" data-testid="save-ai">Kaydet</button>
        </div>

        <div className="card-flat p-4 sm:p-6 lg:p-8">
          <div className="flex justify-between items-start">
            <div>
              <div className="label-caps mb-2">Kur Veri Kaynağı</div>
              <div className="font-display text-2xl mb-4">Canlı Yayın</div>
            </div>
            <button onClick={forceRefresh} disabled={refreshing} className="btn-gold flex items-center gap-2" data-testid="refresh-rates">
              <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
              Şimdi Yenile
            </button>
          </div>

          <div className="space-y-3 mb-6">
            {DATA_SOURCES.map((ds) => (
              <label key={ds.key} className="flex items-start gap-3 border border-white/10 px-4 py-3 cursor-pointer hover:border-[--gold]" data-testid={`data-source-${ds.key}`}>
                <input type="radio" name="ds" checked={dataSource === ds.key} onChange={()=>changeSource(ds.key)} className="mt-1" />
                <div className="text-sm">{ds.label}</div>
              </label>
            ))}
          </div>

          <div className="border-t border-white/5 pt-6">
            <div className="label-caps mb-3">Manuel Kur Güncelleme</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label-caps block mb-2">Metal</label>
                <select value={rateEdit.metal_type} onChange={(e)=>setRateEdit({...rateEdit, metal_type: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="rate-metal">
                  <option value="gold">Altın</option>
                  <option value="silver">Gümüş</option>
                  <option value="platinum">Platin</option>
                </select>
              </div>
              <div>
                <label className="label-caps block mb-2">Alış</label>
                <input type="number" step="0.0001" value={rateEdit.buying_price} onChange={(e)=>setRateEdit({...rateEdit, buying_price: Number(e.target.value)})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="rate-buy" />
              </div>
              <div>
                <label className="label-caps block mb-2">Satış</label>
                <input type="number" step="0.0001" value={rateEdit.selling_price} onChange={(e)=>setRateEdit({...rateEdit, selling_price: Number(e.target.value)})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid="rate-sell" />
              </div>
            </div>
            <button onClick={updateRate} className="btn-primary-gold mt-4" data-testid="save-rate">Kuru Yayınla</button>
          </div>

          <div className="border-t border-white/5 mt-6 pt-4">
            <div className="label-caps mb-3">Mevcut Kurlar</div>
            {["gold","silver","platinum"].map((m) => (
              <div key={m} className="flex justify-between py-2 text-sm">
                <span className="flex items-center gap-2">
                  {m === "gold" ? "Has Altın" : m === "silver" ? "Gümüş" : "Platin"}
                  {rates[m]?.source === "haremaltin" && <span className="label-caps text-emerald-400">Harem · Canlı</span>}
                  {rates[m]?.source === "truncgil" && <span className="label-caps text-emerald-400">Truncgil</span>}
                </span>
                <span className="font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmt4(rates[m]?.selling_price)} ₺</span>
              </div>
            ))}
            <div className="text-xs text-neutral-500 mt-3">
              Kaynaklar: <a href="https://www.haremaltin.com" target="_blank" rel="noreferrer" className="underline hover:text-[--gold]">haremaltin.com</a> · <a href="https://finance.truncgil.com" target="_blank" rel="noreferrer" className="underline hover:text-[--gold]">finance.truncgil.com</a>. Platin her iki kaynakta da güvenilir gram/TRY yayını yapmıyor — manuel güncellenir.
            </div>
          </div>
        </div>
      </div>

      {/* Invoice history */}
      <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="invoice-history-card">
        <div className="flex items-center gap-2 mb-2">
          <Receipt size={16} className="text-neutral-400" />
          <div className="label-caps">Ödeme & Fatura Geçmişi</div>
        </div>
        <div className="font-display text-2xl mb-5">Aboneliğinizin Kayıtları</div>
        {invoices.length === 0 ? (
          <div className="text-sm text-neutral-500 py-4">Henüz kayıtlı bir ödeme işlemi yok. İlk ödemenizden sonra faturalarınız burada listelenecek.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/8">
                <tr>
                  <th className="text-left px-3 py-2">Sipariş No</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-right px-3 py-2">Tutar</th>
                  <th className="text-left px-3 py-2">Oluşturuldu</th>
                  <th className="text-left px-3 py-2">Ödendi</th>
                  <th className="text-left px-3 py-2">Sonraki Dönem</th>
                  <th className="text-center px-3 py-2">Durum</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const st = statusLabel(inv.status);
                  return (
                    <tr key={inv.merchant_oid} className="border-b border-white/6" data-testid={`invoice-row-${inv.merchant_oid}`}>
                      <td className="px-3 py-2 font-mono text-xs">{(inv.merchant_oid || "").slice(0, 12)}…</td>
                      <td className="px-3 py-2">{inv.plan_id}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmtTL(inv.amount_tl)}</td>
                      <td className="px-3 py-2 text-neutral-400 text-xs">{fmtDate(inv.created_at)}</td>
                      <td className="px-3 py-2 text-neutral-400 text-xs">{fmtDate(inv.paid_at)}</td>
                      <td className="px-3 py-2 text-neutral-400 text-xs">{fmtDate(inv.current_period_end)}</td>
                      <td className={`px-3 py-2 text-center text-xs ${st.color}`}>
                        <span className="inline-flex items-center gap-1">{st.icon}{st.txt}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Categories management */}
      <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="categories-card">
        <div className="label-caps">Ürün Kategorileri</div>
        <div className="font-display text-2xl mt-1 mb-4">Varsayılan İşçilik & Marj</div>
        <p className="text-neutral-500 text-xs mb-5 leading-relaxed max-w-2xl">
          Burma, yüzük, kolye gibi kategorilere ayarlayacağınız varsayılan işçilik ve marj değerleri yeni ürün eklerken otomatik dolar. Satış anında POS'ta "Anlık Pazarlık" ile tek tıkla değiştirebilirsiniz.
        </p>

        <div className="overflow-x-auto mb-4">
          <table className="w-full text-sm">
            <thead className="label-caps border-b border-white/8">
              <tr>
                <th className="text-left px-3 py-2">Kategori</th>
                <th className="text-left px-3 py-2">İşçilik Türü</th>
                <th className="text-right px-3 py-2">İşçilik</th>
                <th className="text-right px-3 py-2">Marj %</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id} className="border-b border-white/6" data-testid={`cat-row-${c.name}`}>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2 text-neutral-400 text-xs">
                    {c.default_workmanship_type === "pct" ? "% yüzde" : c.default_workmanship_type === "amount_per_gram" ? "₺ / gram" : "₺ sabit"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{c.default_workmanship_value}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{c.default_margin_pct}%</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => editCategory(c)}
                      className="text-neutral-400 hover:text-[--gold] mr-2 text-xs"
                      data-testid={`cat-edit-${c.name}`}
                    >
                      Düzenle
                    </button>
                    <button
                      onClick={() => deleteCategory(c.id)}
                      className="text-neutral-400 hover:text-red-400"
                      data-testid={`cat-del-${c.name}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-neutral-500 text-sm">Kategori yok</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end p-3 border border-white/8" data-testid="cat-form">
          <div className="sm:col-span-1">
            <label className="label-caps block mb-1.5">Ad</label>
            <input
              value={catDraft.name}
              onChange={(e) => setCatDraft({ ...catDraft, name: e.target.value })}
              placeholder="Burma"
              className="w-full bg-transparent border border-white/15 px-2.5 py-2 text-sm focus:outline-none focus:border-[--gold]"
              data-testid="cat-name"
            />
          </div>
          <div className="sm:col-span-1">
            <label className="label-caps block mb-1.5">Tür</label>
            <select
              value={catDraft.default_workmanship_type}
              onChange={(e) => setCatDraft({ ...catDraft, default_workmanship_type: e.target.value })}
              className="w-full border border-white/15 px-2.5 py-2 text-sm"
              style={{ background: "var(--surface-2)" }}
              data-testid="cat-type"
            >
              <option value="pct">% yüzde</option>
              <option value="amount_per_gram">₺ / gram</option>
              <option value="amount_flat">₺ sabit</option>
            </select>
          </div>
          <div>
            <label className="label-caps block mb-1.5">İşçilik</label>
            <input
              type="number"
              step="0.01"
              value={catDraft.default_workmanship_value}
              onChange={(e) => setCatDraft({ ...catDraft, default_workmanship_value: e.target.value })}
              className="w-full bg-transparent border border-white/15 px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-[--gold]"
              data-testid="cat-workmanship"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Marj %</label>
            <input
              type="number"
              step="0.1"
              value={catDraft.default_margin_pct}
              onChange={(e) => setCatDraft({ ...catDraft, default_margin_pct: e.target.value })}
              className="w-full bg-transparent border border-white/15 px-2.5 py-2 text-sm font-mono focus:outline-none focus:border-[--gold]"
              data-testid="cat-margin"
            />
          </div>
          <button
            onClick={saveCategory}
            className="btn-primary-gold py-2.5"
            data-testid="cat-save"
          >
            Kaydet
          </button>
        </div>
      </div>

      {/* Weekly backup */}
      <div
        className="card-flat p-4 sm:p-6 lg:p-8 relative overflow-hidden"
        data-testid="backup-card"
        style={{ background: "linear-gradient(135deg, rgba(212,175,55,0.06), rgba(0,0,0,0.5))" }}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} style={{ color: "var(--gold)" }} />
              <div className="label-caps">Yedekleme &amp; Güvenlik</div>
            </div>
            <div className="font-display text-2xl">Verileriniz sizinle kalır 🔒</div>
            <p className="text-neutral-400 text-sm mt-2 leading-relaxed max-w-2xl">
              Her Pazartesi 07:00'de <strong className="text-white">tüm stok, müşteri, işlem ve emanet verileriniz</strong> Excel dosyası olarak dükkan sahibinin e-posta adresine otomatik gönderilir. Aşağıdaki butonlarla anında yedek e-postalayabilir veya tek tıkla indirebilirsiniz.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={downloadBackupNow}
              disabled={downloadRunning}
              className="border border-[--gold] text-[--gold] hover:bg-[--gold] hover:text-black px-5 py-3 text-sm flex items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:opacity-60"
              data-testid="download-backup-btn"
            >
              {downloadRunning ? <RefreshCw size={14} className="animate-spin" /> : <Download size={14} />}
              {downloadRunning ? "Hazırlanıyor…" : "Anlık Bulut Yedeği İndir"}
            </button>
            <button
              onClick={runBackup}
              disabled={backupRunning}
              className="btn-primary-gold flex items-center justify-center gap-2 whitespace-nowrap px-5 py-3 disabled:opacity-60"
              data-testid="run-backup-btn"
            >
              {backupRunning ? <RefreshCw size={14} className="animate-spin" /> : <Shield size={14} />}
              {backupRunning ? "Gönderiliyor…" : "E-posta ile Gönder"}
            </button>
          </div>
        </div>
      </div>

      {/* Giriş Güvenliği */}
      <div className="card-flat p-4 sm:p-6 lg:p-8" data-testid="security-card">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} style={{ color: "var(--gold)" }} />
          <div className="label-caps">Giriş Güvenliği</div>
        </div>
        <div className="font-display text-2xl mb-2">İki Faktörlü Doğrulama</div>
        <p className="text-neutral-400 text-sm mb-5 leading-relaxed max-w-2xl">
          Bu özellik açıkken, şifrenizi bilen biri bile e-postanıza gelen 6 haneli kod olmadan hesabınıza giremez.
        </p>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-white/10 mb-6" data-testid="2fa-status">
          <div className="flex-1 min-w-0">
            <div className="text-sm">
              Durum:{" "}
              <span
                className={security.two_factor_enabled ? "text-emerald-400" : "text-neutral-400"}
                data-testid="2fa-state"
              >
                {security.two_factor_enabled ? "AKTİF" : "KAPALI"}
              </span>
            </div>
            <div className="label-caps mt-1">Kod adresi: {security.email || "—"}</div>
          </div>
          <button
            onClick={() => setTwofaModalOpen(true)}
            className={security.two_factor_enabled ? "btn-gold" : "btn-primary-gold"}
            data-testid="2fa-toggle-btn"
          >
            {security.two_factor_enabled ? "Kapat" : "Aç"}
          </button>
        </div>

        {/* Giriş Geçmişi */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-neutral-400" />
            <div className="label-caps">Giriş Geçmişi</div>
          </div>
          <div className="overflow-x-auto border border-white/8" data-testid="login-history">
            <table className="w-full text-xs">
              <thead className="label-caps border-b border-white/8 bg-white/[.02]">
                <tr>
                  <th className="text-left px-3 py-2">Zaman</th>
                  <th className="text-left px-3 py-2">Olay</th>
                  <th className="text-left px-3 py-2">IP</th>
                  <th className="text-left px-3 py-2 hidden md:table-cell">Tarayıcı</th>
                </tr>
              </thead>
              <tbody>
                {loginHistory.slice(0, 15).map((e, i) => (
                  <tr key={i} className="border-b border-white/6">
                    <td className="px-3 py-2 font-mono text-neutral-300 whitespace-nowrap">
                      {new Date(e.at).toLocaleString("tr-TR")}
                    </td>
                    <td className="px-3 py-2">
                      <span className={e.success ? "text-emerald-400" : "text-red-400"}>
                        {e.event === "login_success" ? "Başarılı Giriş"
                          : e.event === "login_fail" ? "Başarısız Giriş"
                          : e.event === "login_2fa_challenge" ? "2FA Kod Gönderildi"
                          : e.event === "login_2fa_fail" ? "2FA Kod Hatalı"
                          : e.event}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-neutral-400">{e.ip || "—"}</td>
                    <td className="px-3 py-2 text-neutral-500 truncate max-w-[300px] hidden md:table-cell" title={e.user_agent}>{e.user_agent || "—"}</td>
                  </tr>
                ))}
                {loginHistory.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-neutral-500">Kayıt yok</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Aktif Oturumlar */}
        <div>
          <div className="flex justify-between items-center gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Monitor size={14} className="text-neutral-400" />
              <div className="label-caps">Aktif Oturumlar</div>
              <span className="text-xs text-neutral-500">({sessions.length})</span>
            </div>
            {sessions.length > 1 && (
              <button
                onClick={revokeAllSessions}
                className="border border-red-400/40 text-red-300 hover:bg-red-500/10 px-3 py-2 text-xs inline-flex items-center gap-1.5 transition-colors"
                data-testid="revoke-all-btn"
              >
                <LogOut size={11} /> Diğer Oturumları Kapat
              </button>
            )}
          </div>
          <div className="space-y-2" data-testid="sessions-list">
            {sessions.map((s) => (
              <div key={s.jti} className="flex items-center justify-between gap-3 p-3 border border-white/8" data-testid={`session-${s.jti?.slice(0,8)}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm flex items-center gap-2">
                    {s.current && (
                      <span className="text-[10px] font-mono tracking-wider px-1.5 py-0.5 border border-[--gold] text-[--gold]">
                        BU OTURUM
                      </span>
                    )}
                    <span className="font-mono text-neutral-300">{s.ip || "—"}</span>
                  </div>
                  <div className="label-caps mt-1 truncate max-w-full" title={s.user_agent}>
                    {new Date(s.created_at).toLocaleString("tr-TR")} · {s.user_agent?.slice(0, 60) || "—"}
                  </div>
                </div>
                {!s.current && (
                  <button
                    onClick={() => revokeSession(s.jti)}
                    className="shrink-0 text-neutral-400 hover:text-red-400 text-xs inline-flex items-center gap-1"
                    data-testid={`revoke-${s.jti?.slice(0,8)}`}
                  >
                    <LogOut size={11} /> Kapat
                  </button>
                )}
              </div>
            ))}
            {sessions.length === 0 && (
              <div className="text-center py-6 text-neutral-500 text-sm">Aktif oturum yok</div>
            )}
          </div>
        </div>
      </div>

      {/* 2FA modal */}
      {twofaModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm"
          onClick={() => !twofaBusy && setTwofaModalOpen(false)}
          data-testid="2fa-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm card-flat p-5 sm:p-6"
            style={{ background: "var(--surface)" }}
          >
            <div className="label-caps flex items-center gap-1.5 mb-2">
              <ShieldCheck size={12} style={{ color: "var(--gold)" }} />
              İki Faktörlü Doğrulama
            </div>
            <div className="font-display text-xl mb-3">
              {security.two_factor_enabled ? "Kapatmayı Onayla" : "Etkinleştirmeyi Onayla"}
            </div>
            <p className="text-neutral-400 text-xs mb-4 leading-relaxed">
              Kimliğinizi doğrulamak için mevcut parolanızı girin.
            </p>
            <input
              type="password"
              placeholder="Parolanız"
              value={twofaPassword}
              onChange={(e) => setTwofaPassword(e.target.value)}
              autoFocus
              className="w-full bg-transparent border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-[--gold] mb-4"
              data-testid="2fa-password"
              onKeyDown={(e) => e.key === "Enter" && submitTwoFactor(security.two_factor_enabled ? "disable" : "enable")}
            />
            <div className="flex gap-2">
              <button
                onClick={() => setTwofaModalOpen(false)}
                disabled={twofaBusy}
                className="flex-1 border border-white/15 hover:border-white/30 py-2.5 text-sm transition-colors"
              >
                Vazgeç
              </button>
              <button
                onClick={() => submitTwoFactor(security.two_factor_enabled ? "disable" : "enable")}
                disabled={twofaBusy}
                className="btn-primary-gold flex-1 py-2.5 disabled:opacity-60"
                data-testid="2fa-submit"
              >
                {twofaBusy ? "Kaydediliyor…" : (security.two_factor_enabled ? "Kapat" : "Aç")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChangePasswordModal open={pwOpen} onClose={()=>setPwOpen(false)} onDone={()=>{ setPwOpen(false); toast.success("Şifre güncellendi"); }} />
    </div>
  );
}